import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { rowTotalQty } from './helpers';

// Per-row multi-size selector: tick individual sizes (each at its own qty/rate)
// or use the quick set-range buttons (e.g. "M–XXL", "3XL–5XL") to tick a whole
// band at once. One master Qty applies to every ticked size and can be edited
// per size here.
const SizePopover = ({
  idx, row, toggleSize, applyBand, setSizeQty, setSizeRate, selectAllSizes, clearSizes, large,
  setMasterQty, autoOpen, onAutoOpenConsumed, onDone, onOpenEmpty,
}) => {
  const [open, setOpen] = useState(false);
  const sizeQty = row.sizeQty || {};
  const selected = Object.keys(sizeQty);
  const totalPcs = rowTotalQty(row);
  const disabled = !row.color || (row.individualSizes || []).length === 0;

  // Auto-open in the fast flow (after a colour is chosen). Also copy the
  // previous row's size run when this row has none yet.
  useEffect(() => {
    if (autoOpen && !disabled) {
      if (selected.length === 0 && onOpenEmpty) onOpenEmpty(idx);
      setOpen(true);
      onAutoOpenConsumed && onAutoOpenConsumed(idx);
    }
    // eslint-disable-next-line
  }, [autoOpen]);

  const handleOpenChange = (o) => {
    if (o && selected.length === 0 && onOpenEmpty) onOpenEmpty(idx);
    setOpen(o);
    if (!o && onDone) onDone(idx);
  };

  const summary = selected.length === 0
    ? 'Select sizes'
    : `${selected.length} size${selected.length > 1 ? 's' : ''} · ${totalPcs} pc${totalPcs !== 1 ? 's' : ''}`;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`so-size-trigger-${idx}`}
          className={`${large ? 'h-11 text-base' : 'h-8 text-sm'} w-full rounded-md border px-2 text-left flex items-center justify-between ${disabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-background hover:border-sky-400'} ${selected.length ? 'border-sky-300 text-sky-800 font-medium' : 'border-input text-gray-500'}`}
        >
          <span className="truncate">{summary}</span>
          <svg className="w-3.5 h-3.5 ml-1 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 z-50" align="start" data-testid={`so-size-popover-${idx}`}>
        {/* Qty-each + Quick set-range buttons */}
        <div className="p-2 border-b bg-gray-50/80 space-y-2">
          {setMasterQty && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Qty each</span>
              <Input type="number" min="0" value={row.qty ?? ''} placeholder="e.g. 10"
                onChange={e => setMasterQty(idx, e.target.value)} onWheel={e => e.target.blur()}
                className="h-7 text-sm flex-1" data-testid={`so-qty-each-${idx}`} />
            </div>
          )}
          <div>
            <div className="text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Quick sets</div>
            <div className="flex flex-wrap gap-1">
              {(row.bands || []).map(b => {
                const active = b.sizes.every(s => s in sizeQty);
                return (
                  <Button key={b.id} type="button" size="sm" variant={active ? 'default' : 'outline'}
                    className={`h-6 px-2 text-xs ${active ? 'bg-sky-600 hover:bg-sky-700' : 'border-sky-300 text-sky-700 hover:bg-sky-50'}`}
                    onClick={() => applyBand(idx, b)} data-testid={`so-band-${b.id}-${idx}`}>
                    {b.label}
                  </Button>
                );
              })}
              <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => selectAllSizes(idx)} data-testid={`so-size-all-${idx}`}>All</Button>
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => clearSizes(idx)} data-testid={`so-size-clear-${idx}`}>Clear</Button>
            </div>
          </div>
        </div>
        {/* Per-size tick list */}
        <div className="max-h-64 overflow-y-auto p-2 space-y-1">
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 px-1 pb-1 text-[10px] font-semibold text-gray-400 uppercase">
            <span></span><span>Size</span><span className="text-right">Qty</span><span className="text-right">Rate</span>
          </div>
          {(row.individualSizes || []).map(s => {
            const checked = s.size in sizeQty;
            return (
              <div key={s.size} className={`grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 px-1 py-0.5 rounded ${checked ? 'bg-sky-50/60' : ''}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleSize(idx, s.size)} data-testid={`so-size-check-${s.size}-${idx}`} />
                <button type="button" className="text-left text-sm font-medium text-gray-700" onClick={() => toggleSize(idx, s.size)}>{s.size}</button>
                <Input type="number" min="0" value={checked ? (sizeQty[s.size] ?? '') : ''} disabled={!checked}
                  onChange={e => setSizeQty(idx, s.size, e.target.value)} onWheel={e => e.target.blur()}
                  className="h-7 w-16 text-right text-xs" data-testid={`so-size-qty-${s.size}-${idx}`} />
                <Input type="number" min="0" step="0.01" value={(row.sizeRate || {})[s.size] ?? ''} disabled={!checked}
                  onChange={e => setSizeRate(idx, s.size, e.target.value)} onWheel={e => e.target.blur()}
                  className="h-7 w-20 text-right text-xs" data-testid={`so-size-rate-${s.size}-${idx}`} />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs bg-gray-50/80">
          <span className="text-gray-500">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{totalPcs} pcs · ₹{(row.amount || 0).toLocaleString('en-IN')}</span>
            <Button type="button" size="sm" className="h-7 bg-sky-600 hover:bg-sky-700" onClick={() => handleOpenChange(false)} data-testid={`so-size-done-${idx}`}>Done</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SizePopover;
